import React, { useState, useEffect, useRef } from 'react';
import {
    FaChartBar, FaCog, FaSignOutAlt, FaMoon, FaSun, FaQuestionCircle,
    FaGamepad, FaTrophy, FaStar, FaCircle
} from 'react-icons/fa';
import { auth } from './firebase';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const Navbar = ({ isLightMode, toggleTheme }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [userName, setUserName] = useState('');
    const [userLevel, setUserLevel] = useState(1);
    const dropdownRef = useRef(null);
    const profileRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && 
                !dropdownRef.current.contains(event.target) && 
                profileRef.current && 
                !profileRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }

        // Close dropdown when ESC key is pressed
        function handleEscKey(event) {
            if (event.key === 'Escape') {
                setDropdownOpen(false);
            }
        }

        // Add event listeners when dropdown is open
        if (dropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("keydown", handleEscKey);
        }
        
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [dropdownOpen]);

    // Close dropdown when navigating
    useEffect(() => {
        const handleNavigation = () => setDropdownOpen(false);
        window.addEventListener('popstate', handleNavigation);
        
        return () => window.removeEventListener('popstate', handleNavigation);
    }, []);

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    useEffect(() => {
        const db = getFirestore();

        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                setUser(user);
                if (user.displayName) {
                    setUserName(user.displayName);
                } else {
                    const userDocRef = doc(db, 'users', user.uid);
                    try {
                        const userDocSnap = await getDoc(userDocRef);
                        if (userDocSnap.exists()) {
                            const userData = userDocSnap.data();
                            setUserName(`${userData.firstName || ''} ${userData.lastName || ''}`);
                            setUserLevel(userData.level || 1);
                        } else {
                            setUserName('User');
                        }
                    } catch (error) {
                        console.error("Error fetching user data:", error);
                        setUserName('User');
                    }
                }
            } else {
                setUser(null);
                setUserName('');
            }
        });

        return () => unsubscribe();
    }, []);

    const handleSignOut = () => {
        auth.signOut().then(() => {
            console.log('User signed out');
            setDropdownOpen(false);
        }).catch((error) => {
            console.error('Error signing out:', error);
        });
    };

    // CSS Classes
    const navClasses = isLightMode 
        ? "bg-[var(--navbar-background-color)] text-[var(--navbar-text-color)]" 
        : "bg-[var(--navbar-background-color)] text-[var(--navbar-text-color)]";
        
    const buttonClasses = isLightMode
        ? "bg-[var(--interactive-color)] hover:bg-[var(--link-hover-color)] text-white"
        : "bg-[var(--interactive-color)] hover:bg-[var(--link-hover-color)] text-white";
        
    const dropdownClasses = isLightMode
        ? "bg-white text-[var(--text-color)] shadow-2xl border border-gray-200"
        : "bg-gray-900 text-white shadow-2xl border border-gray-700";

    return (
        <nav className={`flex justify-between items-center px-8 h-[74px] text-lg ${navClasses} flex-wrap md:flex-nowrap sticky top-0 z-50`}>
            <a href="/" className="text-current no-underline hover:opacity-90 focus:outline focus:outline-[var(--focus-outline-color)]" aria-label="Quiz Arcade - Go to homepage">
                <div className="text-2xl font-bold font-['Jersey_25',sans-serif]">Quiz Arcade</div>
            </a>

            <div className="flex items-center gap-12">
                <ul className="flex gap-10 list-none text-lg">
                    <li>
                        <a href="/" className="text-current no-underline relative px-4 py-2 transition-colors duration-300 hover:text-white hover:bg-[var(--interactive-color)] rounded-md">
                            Home
                        </a>
                    </li>
                    <li>
                        <a href="/games" className="text-current no-underline relative px-4 py-2 transition-colors duration-300 hover:text-white hover:bg-[var(--interactive-color)] rounded-md">
                            Games
                        </a>
                    </li>
                    <li>
                        <a href="/leaderboards" className="text-current no-underline relative px-4 py-2 transition-colors duration-300 hover:text-white hover:bg-[var(--interactive-color)] rounded-md">
                            Leaderboard
                        </a>
                    </li>
                </ul>

                {/* Survey Button styled like other navbar links */}
                <a 
                    href="https://forms.gle/TPJdmddipzWWUEqPA" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-current no-underline relative px-4 py-2 transition-colors duration-300 hover:text-white hover:bg-[var(--interactive-color)] rounded-md flex items-center gap-2"
                    aria-label="Take our user survey"
                >
                    <FaQuestionCircle className="text-lg" />
                    <span>Survey</span>
                </a>

                <button 
                    className="bg-transparent border-none text-current cursor-pointer mr-4 p-2 rounded-full flex items-center justify-center transition-colors duration-300 hover:bg-white/10 gap-2"
                    onClick={toggleTheme}
                    aria-label={isLightMode ? "Switch to dark mode" : "Switch to light mode"}
                >
                    {isLightMode ? (
                        <>
                            <FaMoon className="text-lg" />
                            <span className="text-sm font-medium">Dark Mode</span>
                        </>
                    ) : (
                        <>
                            <FaSun className="text-lg" />
                            <span className="text-sm font-medium">Light Mode</span>
                        </>
                    )}
                </button>

                {user ? (
                    <div className="relative">
                        <div 
                            ref={profileRef}
                            onClick={toggleDropdown}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleDropdown();
                                }
                            }} 
                            tabIndex="0"
                            role="button"
                            aria-expanded={dropdownOpen}
                            aria-haspopup="true"
                            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 cursor-pointer focus:outline-2 focus:outline-[var(--focus-outline-color)]"
                        >

                            {/* Profile image without level overlay */}
                            <div className="w-[45px] h-[45px] overflow-hidden rounded-full border-2 border-[var(--interactive-color)] transition-all duration-300 hover:border-[3px] shadow-lg">
                                <img 
                                    src={user.photoURL || '/profilepicture.png'} 
                                    alt="Profile" 
                                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                    onError={(e) => {e.target.src = '/profilepicture.png'}}
                                />
                            </div>
                        </div>
                        
                        {/* Enhanced dropdown with simplified menu */}
                        {dropdownOpen && (
                            <div 
                                ref={dropdownRef}
                                className={`absolute top-[70px] right-0 rounded-xl w-[320px] z-[1000] overflow-hidden ${dropdownClasses} backdrop-blur-sm animate-dropdown`}
                            >
                                {/* User profile header with glass effect */}
                                <div className="relative overflow-hidden">
                                    {/* Background gradient with overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-[var(--interactive-color)] to-[var(--accent-color)]"></div>
                                    <div className="absolute inset-0 bg-black bg-opacity-20 backdrop-blur-sm"></div>
                                    
                                    {/* User profile content */}
                                    <div className="relative p-6">
                                        <div className="flex items-center">
                                            {/* Profile details on left */}
                                            <div className="flex-shrink-0 relative">
                                                <div className="w-16 h-16 rounded-full border-3 border-white shadow-lg overflow-hidden">
                                                    <img 
                                                        src={user.photoURL || '/profilepicture.png'} 
                                                        alt="" 
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {e.target.src = '/profilepicture.png'}}
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* User details on right */}
                                            <div className="ml-4 flex-1">
                                                <div className="font-bold text-xl text-white">{userName || 'User'}</div>
                                                <div className="text-sm text-white/80">{user.email}</div>
                                                
                                                {/* Custom styled level badge */}
                                                <div className="flex items-center gap-2 mt-2">
                                                    <div className="bg-green-400/90 px-3 py-1 rounded-full flex items-center shadow-sm">
                                                        <span className="text-xs font-bold text-green-900">Level {userLevel}</span>
                                                    </div>
                                                    <span className="text-xs text-white">• 250 XP</span>
                                                </div>
                                                
                                                {/* XP Progress bar */}
                                                <div className="mt-2">
                                                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-white rounded-full"
                                                            style={{ width: '65%' }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Simplified menu items with guaranteed visibility */}
                                <div className="p-2 space-y-1">
                                    {/* My Stats */}
                                    <a 
                                        href="/stats" 
                                        className="group flex items-center p-4 rounded-lg text-[var(--text-color)] no-underline hover:bg-[var(--interactive-color)] hover:text-white focus:bg-[var(--interactive-color)] focus:text-white focus:outline-none transition-all duration-300 w-full"
                                    >
                                        <div className="flex-shrink-0 w-10 h-10 bg-[#3a86ff] bg-opacity-15 flex items-center justify-center rounded-full mr-4 group-hover:bg-white group-hover:bg-opacity-20">
                                            <FaChartBar size={20} color="#3a86ff" className="group-hover:text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-medium">My Stats</div>
                                            <div className="text-sm opacity-70 group-hover:opacity-90">View your game statistics</div>
                                        </div>
                                    </a>
                                    
                                    {/* Settings */}
                                    <a 
                                        href="/profile-settings" 
                                        className="group flex items-center p-4 rounded-lg text-[var(--text-color)] no-underline hover:bg-[var(--interactive-color)] hover:text-white focus:bg-[var(--interactive-color)] focus:text-white focus:outline-none transition-all duration-300 w-full"
                                    >
                                        <div className="flex-shrink-0 w-10 h-10 bg-[#3a86ff] bg-opacity-15 flex items-center justify-center rounded-full mr-4 group-hover:bg-white group-hover:bg-opacity-20">
                                            <FaCog size={20} color="#3a86ff" className="group-hover:text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-medium">Settings</div>
                                            <div className="text-sm opacity-70 group-hover:opacity-90">Manage your account</div>
                                        </div>
                                    </a>
                                    
                                </div>
                                
                                <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                                    
                                {/* Logout Button */}
                                <button 
                                    onClick={handleSignOut} 
                                    className="group flex items-center p-4 rounded-lg w-full text-left bg-transparent border-none cursor-pointer hover:bg-red-500/10 focus:bg-red-500/10 focus:outline-none transition-all duration-300"
                                >
                                    <div className="w-10 h-10 flex items-center justify-center bg-red-500 bg-opacity-10 rounded-full mr-4 group-hover:bg-red-500 group-hover:bg-opacity-20 transition-all duration-300">
                                        <FaSignOutAlt className="text-xl text-red-500" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-red-500">Log Out</div>
                                        <div className="text-sm text-red-500/70">Sign out from your account</div>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <a href="/login" className={`rounded-lg border-none cursor-pointer font-medium h-[40px] leading-[20px] py-[10px] px-[16px] text-center no-underline transition-colors duration-300 ${buttonClasses}`}>
                        Login
                    </a>
                )}
            </div>
        </nav>
    );
};

export default Navbar;